import { useCallback, useMemo, useRef, useState, type ReactElement } from 'react';
import { percent, taskId as castTaskId, type Dependency, type ExpectedProgress, type ResourceId, type ResourceRateTables, type Task, type TaskId } from '@bigint-project/core';

import { GanttChart } from '../components/gantt';
import { TaskGrid } from '../components/grid';
import { TaskAssignmentPanel } from '../components/assignment';
import { ResourceRateTablesDialog } from '../components/resource-rate-tables';
import type { PasteImportResult } from '../components/grid/paste-import';
import type { ColumnDef as GridColumnDef, SelectMode } from '../components/grid/types';
import {
  buildAvailableGridColumns,
  buildGanttTasks,
  selectVisibleTasks,
  toGridColumns,
  toStoreColumns,
} from '../integration/adapters';
import { progressAsOf, selectExpectedProgressByTask, selectResourceNamesByTask } from '../integration/derivations';
import { buildTaskDatePatch } from '../integration/task-edits';
import { useProjectStore } from '../store/project-store';

const MIN_RATIO = 0.15;
const MAX_RATIO = 0.85;
const DEFAULT_RATIO = 0.42;

/**
 * 表格與甘特圖共用的列高。
 *
 * 必須是**同一個常數**：兩者共用 `scrollTop`，列高只要差一個像素，捲到第 100 列
 * 時左右兩邊就會差 100px，完全對不上。這也是為什麼列高不做成各自的視圖設定。
 */
export const ROW_HEIGHT = 28;

/**
 * 左右分割版面：任務表格 | 甘特圖。
 *
 * 兩側都是純展示元件（props in / callbacks out），本檔負責把 store 接上去：
 * - 可見列由 `selectVisibleTasks()` 算一次給兩邊共用（同一份順序才對得齊）
 * - `scrollTop` / `selectedTaskIds` 都放在 store，兩側受控 → 自動同步
 * - 所有 callback 直接對應 store command，不在這裡做任何領域邏輯
 */
export function GanttSplitView({ readOnly = false }: { readonly readOnly?: boolean }): ReactElement {
  const [ratio, setRatio] = useState(DEFAULT_RATIO);
  const [rateTablesResourceId, setRateTablesResourceId] = useState<ResourceId | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef(false);

  const project = useProjectStore((state) => state.project);
  const collapsedTaskIds = useProjectStore((state) => state.collapsedTaskIds);
  const selectedTaskIds = useProjectStore((state) => state.selectedTaskIds);
  const storeColumns = useProjectStore((state) => state.columns);
  const timeScale = useProjectStore((state) => state.timeScale);
  const showBaseline = useProjectStore((state) => state.showBaseline);
  const showCriticalPath = useProjectStore((state) => state.showCriticalPath);
  const dependencyLineMode = useProjectStore((state) => state.dependencyLineMode);
  const scrollTop = useProjectStore((state) => state.scrollTop);
  const setScrollTop = useProjectStore((state) => state.setScrollTop);
  const toggleCollapsed = useProjectStore((state) => state.toggleCollapsed);
  const setColumns = useProjectStore((state) => state.setColumns);
  const setTimeScale = useProjectStore((state) => state.setTimeScale);
  const pasteWarning = useProjectStore((state) => state.pasteWarning);
  const taskEditWarning = useProjectStore((state) => state.taskEditWarning);
  const dismissTaskEditWarning = useProjectStore((state) => state.dismissTaskEditWarning);
  const dismissPasteWarning = useProjectStore((state) => state.dismissPasteWarning);

  const visibleTasks = useMemo(
    () => selectVisibleTasks(project, collapsedTaskIds),
    [project, collapsedTaskIds],
  );
  const ganttTasks = useMemo(
    () => buildGanttTasks(project, collapsedTaskIds),
    [project, collapsedTaskIds],
  );
  const gridColumns = useMemo(
    () => toGridColumns(storeColumns, project.customFields),
    [project.customFields, storeColumns],
  );
  const availableGridColumns = useMemo(
    () => buildAvailableGridColumns(project.customFields),
    [project.customFields],
  );
  // derivations 的 map 以 string 為 key(全專案共用的 memo);grid 要的是 TaskId。
  const resourceNamesByTask = useMemo(
    () => selectResourceNamesByTask(project) as ReadonlyMap<TaskId, string>,
    [project],
  );
  const expectedProgressByTask = useMemo(
    () => selectExpectedProgressByTask(project, progressAsOf(project)) as ReadonlyMap<TaskId, ExpectedProgress>,
    [project],
  );
  const typedSelection = useMemo(
    () => selectedTaskIds.map((id) => castTaskId(id)),
    [selectedTaskIds],
  );
  const selectedTask = useMemo(
    () => (selectedTaskIds.length === 1 ? project.tasks.find((task) => task.id === selectedTaskIds[0]) ?? null : null),
    [project.tasks, selectedTaskIds],
  );
  const selectedTaskAssignments = useMemo(
    () => (selectedTask === null ? [] : project.assignments.filter((assignment) => assignment.taskId === selectedTask.id)),
    [project.assignments, selectedTask],
  );
  const rateTablesResource = rateTablesResourceId === null
    ? null
    : project.resources.find((resource) => resource.id === rateTablesResourceId) ?? null;

  // --- 選取 ---------------------------------------------------------------

  const selectTask = useCallback((id: TaskId, mode: SelectMode): void => {
    const store = useProjectStore.getState();
    const current = store.selectedTaskIds;
    if (mode === 'additive') {
      store.selectTasks(
        current.includes(id as string) ? current.filter((v) => v !== (id as string)) : [...current, id as string],
      );
      return;
    }
    if (mode === 'range' && current.length > 0) {
      const ordered = selectVisibleTasks(store.project, store.collapsedTaskIds).map((t) => t.id as string);
      const anchor = ordered.indexOf(current[current.length - 1] ?? '');
      const target = ordered.indexOf(id as string);
      if (anchor !== -1 && target !== -1) {
        const [from, to] = anchor <= target ? [anchor, target] : [target, anchor];
        store.selectTasks(ordered.slice(from, to + 1));
        return;
      }
    }
    store.selectTasks([id as string]);
  }, []);

  // --- 表格 callback ------------------------------------------------------

  const handleTaskChange = useCallback((id: TaskId, patch: Partial<Task>): void => {
    const { id: _ignored, percentComplete, ...rest } = patch;
    const store = useProjectStore.getState();
    // 完成百分比不能直接寫進 Task：指派的實際／剩餘工時與看板狀態都要跟著動，
    // 這些規則只在 core 的 setTaskProgress 裡（依任務的 progressMode 分歧）。
    if (percentComplete !== undefined) store.setTaskProgress({ taskId: id, percentComplete });
    if (Object.keys(rest).length > 0) store.updateTask(id, rest);
  }, []);

  const handleDependencyChange = useCallback((id: TaskId, next: readonly Dependency[]): void => {
    useProjectStore.getState().replaceTaskDependencies(id, next);
  }, []);

  const handleTaskInsert = useCallback((afterTaskId: TaskId | null): void => {
    const store = useProjectStore.getState();
    const anchor = afterTaskId === null ? undefined : store.project.tasks.find((t) => t.id === afterTaskId);
    const newId = store.insertTask({
      name: '新增任務',
      ...(afterTaskId === null ? {} : { afterTaskId }),
      parentId: anchor?.parentId ?? null,
    });
    store.selectTasks([newId as string]);
  }, []);

  const handleInsertMilestone = useCallback((afterTaskId: TaskId | null): void => {
    const store = useProjectStore.getState();
    const anchor = afterTaskId === null ? undefined : store.project.tasks.find((t) => t.id === afterTaskId);
    store.insertTask({
      name: '新增里程碑',
      ...(afterTaskId === null ? {} : { afterTaskId }),
      parentId: anchor?.parentId ?? null,
      overrides: { isMilestone: true, durationMinutes: 0 as Task['durationMinutes'], work: 0 as Task['work'] },
    });
  }, []);

  const handleInsertSummary = useCallback((afterTaskId: TaskId | null): void => {
    const store = useProjectStore.getState();
    const anchor = afterTaskId === null ? undefined : store.project.tasks.find((t) => t.id === afterTaskId);
    store.insertTask({
      name: '新增摘要任務',
      ...(afterTaskId === null ? {} : { afterTaskId }),
      parentId: anchor?.parentId ?? null,
      overrides: { isSummary: true },
    });
  }, []);

  const handleTaskDelete = useCallback((ids: readonly TaskId[]): void => {
    const store = useProjectStore.getState();
    store.deleteTasks(ids);
    store.selectTasks([]);
  }, []);

  /**
   * 外部（Excel / MS Project 匯出表）整批貼上。
   *
   * TaskGrid 只做解析與 ref 解析，實際建立列與依賴在 store 的 `pasteTasks()`
   * ——它整批走一次 `commit()`，所以 114 列是單一 undo 步驟。
   */
  const handleTasksPaste = useCallback((result: PasteImportResult): void => {
    useProjectStore.getState().pasteTasks(result);
  }, []);

  const handleColumnsChange = useCallback(
    (columns: readonly GridColumnDef[]): void => {
      setColumns(toStoreColumns(columns));
    },
    [setColumns],
  );

  // --- 甘特圖 callback ----------------------------------------------------

  const handleTaskDateChange = useCallback(
    (id: TaskId, newStart: Task['start'], newFinish: Task['finish']): void => {
      const store = useProjectStore.getState();
      const task = store.project.tasks.find((t) => t.id === id);
      if (task === undefined) return;
      store.updateTask(id, buildTaskDatePatch(store.project, task, newStart, newFinish));
    },
    [],
  );

  const handleProgressChange = useCallback((id: TaskId, value: Task['percentComplete']): void => {
    const clamped = Math.min(100, Math.max(0, value as number));
    useProjectStore.getState().setTaskProgress({ taskId: id, percentComplete: percent(clamped) });
  }, []);

  /**
   * 滾輪縮放跨過刻度切點時，把「實際生效的刻度」同步回上方下拉選單——否則表頭
   * 已經切成逐日、選單卻還寫著「月」，選單等於在說謊。
   *
   * 不會形成迴圈：`setTimeScale()` 對相同值直接 return（見 store 內的註解），
   * 而甘特圖只在生效刻度**改變**時才回報，所以「回報 → 設定 → 重設 pxPerDay →
   * 生效刻度不變 → 不再回報」，最多一來一回就收斂。
   */
  const handleEffectiveTimeScaleChange = useCallback(
    (scale: typeof timeScale): void => {
      setTimeScale(scale);
    },
    [setTimeScale],
  );

  const handleDependencyCreate = useCallback(
    (predecessorId: TaskId, successorId: TaskId, type: Dependency['type']): void => {
      useProjectStore.getState().addDependency({ predecessorId, successorId, type });
    },
    [],
  );

  // --- 分隔線拖曳 ---------------------------------------------------------

  const onPointerMove = useCallback((event: PointerEvent) => {
    if (!draggingRef.current || containerRef.current === null) return;
    const rect = containerRef.current.getBoundingClientRect();
    const next = (event.clientX - rect.left) / rect.width;
    setRatio(Math.min(MAX_RATIO, Math.max(MIN_RATIO, next)));
  }, []);

  const stopDragging = useCallback(() => {
    draggingRef.current = false;
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', stopDragging);
  }, [onPointerMove]);

  const startDragging = useCallback(() => {
    draggingRef.current = true;
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', stopDragging);
  }, [onPointerMove, stopDragging]);

  // 空專案時右半邊換成提示，但**左半邊的表格照常渲染** —— 「貼上整份 WBS 表」
  // 最常見的入口就是空專案，表格不在畫面上就沒有東西可以接收貼上事件。
  const isEmpty = project.tasks.length === 0;

  return (
    <div className="flex h-full w-full min-h-0 flex-col">
      {taskEditWarning === null ? null : (
        <div
          data-testid="task-edit-warning"
          role="status"
          className="flex shrink-0 items-start justify-between gap-2 border-b border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200"
        >
          <span>{taskEditWarning}</span>
          <button type="button" onClick={dismissTaskEditWarning} className="shrink-0 font-medium underline">
            關閉
          </button>
        </div>
      )}
      {pasteWarning === null ? null : (
        <div
          data-testid="paste-warning"
          role="status"
          className="flex shrink-0 items-start justify-between gap-2 border-b border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200"
        >
          <span>{pasteWarning}</span>
          <button type="button" onClick={dismissPasteWarning} className="shrink-0 font-medium underline">
            關閉
          </button>
        </div>
      )}
      <div ref={containerRef} data-testid="gantt-split-view" className="flex w-full min-h-0 flex-1">
        <div style={{ width: `${ratio * 100}%` }} className="h-full min-w-0">
        <fieldset disabled={readOnly} className="h-full min-w-0 border-0 p-0">
          <TaskGrid
          tasks={visibleTasks}
          columns={gridColumns}
          collapsedTaskIds={collapsedTaskIds as readonly TaskId[]}
          selectedTaskIds={typedSelection}
          dependencies={project.dependencies}
          resourceNamesByTask={resourceNamesByTask}
          expectedProgressByTask={expectedProgressByTask}
          availableColumns={availableGridColumns}
          rowHeight={ROW_HEIGHT}
          scrollTop={scrollTop}
          onScrollTopChange={setScrollTop}
          onTaskChange={handleTaskChange}
          customFields={project.customFields}
          onCustomFieldChange={(taskId, fieldId, value) => {
            useProjectStore.getState().setTaskCustomFieldValue(taskId, fieldId, value);
          }}
          onDependencyChange={handleDependencyChange}
          onTaskInsert={readOnly ? () => undefined : handleTaskInsert}
          onTaskDelete={handleTaskDelete}
          onIndent={(ids) => useProjectStore.getState().indentTasks(ids)}
          onOutdent={(ids) => useProjectStore.getState().outdentTasks(ids)}
          onToggleCollapse={(id) => toggleCollapsed(id as string)}
          onTaskSelect={selectTask}
          onColumnsChange={handleColumnsChange}
          onInsertMilestone={readOnly ? () => undefined : handleInsertMilestone}
          onInsertSummary={readOnly ? () => undefined : handleInsertSummary}
          onTasksPaste={handleTasksPaste}
          />
        </fieldset>
        </div>
        <div
          role="separator"
          aria-orientation="vertical"
          onPointerDown={startDragging}
          className="w-1 shrink-0 cursor-col-resize touch-none bg-slate-200 hover:bg-blue-400 dark:bg-slate-700 dark:hover:bg-blue-500"
        />
        <div style={{ width: `${(1 - ratio) * 100}%` }} className="h-full min-w-0">
          {isEmpty ? (
            <div
              data-testid="gantt-split-empty"
              className="flex h-full w-full items-center justify-center rounded border border-dashed border-slate-300 text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400"
            >
              <div className="text-center">
                <p className="font-medium">這個專案還沒有任務</p>
                <p className="mt-1 text-xs">
                  用工具列的「載入範例」看看完整功能，或在左側表格中新增第一筆任務、直接貼上 Excel 的 WBS 表。
                </p>
              </div>
            </div>
          ) : (
            <GanttChart
              tasks={ganttTasks}
              dependencies={project.dependencies}
              timeScale={timeScale}
              onEffectiveTimeScaleChange={handleEffectiveTimeScaleChange}
              rowHeight={ROW_HEIGHT}
              showBaseline={showBaseline}
              showCriticalPath={showCriticalPath}
              dependencyLineMode={dependencyLineMode}
              scrollTop={scrollTop}
              onScrollTopChange={setScrollTop}
              onTaskDateChange={readOnly ? () => undefined : handleTaskDateChange}
              onTaskProgressChange={readOnly ? () => undefined : handleProgressChange}
              onDependencyCreate={readOnly ? () => undefined : handleDependencyCreate}
              onTaskSelect={(id, additive) => selectTask(id, additive ? 'additive' : 'single')}
              selectedTaskIds={typedSelection}
            />
          )}
        </div>
      </div>
      <fieldset disabled={readOnly} className="border-0 p-0">
        <TaskAssignmentPanel
          task={selectedTask}
          selectionCount={selectedTaskIds.length}
          resources={project.resources}
          assignments={selectedTaskAssignments}
          onAssign={(resourceId) => {
            if (selectedTask !== null && !selectedTask.isSummary) {
              // 新指派預設為 100% 投入，工時等於目前任務工期；若只建立一筆
              // work=0 的指派，資源使用量、超額配置與成本畫面都會看似「沒有作用」。
              useProjectStore.getState().assignResource({
                taskId: selectedTask.id,
                resourceId,
                units: 1,
                work: selectedTask.durationMinutes as number,
              });
            }
          }}
          onUnassign={(assignmentId) => useProjectStore.getState().unassignResource(assignmentId)}
          onRateTableChange={(assignmentId, table) => useProjectStore.getState().setAssignmentRateTable(assignmentId, table)}
          onOpenRateTables={setRateTablesResourceId}
        />
      </fieldset>
      {readOnly || rateTablesResource === null ? null : (
        <ResourceRateTablesDialog
          resource={rateTablesResource}
          open
          onCancel={() => setRateTablesResourceId(null)}
          onSave={(rateTables: ResourceRateTables) => {
            useProjectStore.getState().updateResource(rateTablesResource.id, { rateTables });
            setRateTablesResourceId(null);
          }}
        />
      )}
    </div>
  );
}
